import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SvMessageService } from './sv-message.service';
import { SvMessageResult } from '../components/sv-message/sv-message.component';
import { PT_FALLBACK } from '../../i18n/pt-fallback';

/**
 * Serviço centralizado para alertas e diálogos modais
 * Fornece uma interface consistente e profissional para mensagens ao utilizador
 * Usa o componente SvMessage nativo internamente
 */
@Injectable({
  providedIn: 'root',
})
export class AlertService {
  constructor(
    private translate: TranslateService,
    private svMessage: SvMessageService,
  ) {}

  /**
   * Verifica se o utilizador está online
   * @private
   */
  private isOnline(): boolean {
    return window.navigator.onLine;
  }

  /**
   * Converte SvMessageResult para um formato compatível com SweetAlertResult
   */
  private toSwalResult(result: SvMessageResult): any {
    return {
      isConfirmed: result.isConfirmed,
      isDismissed: result.isDismissed,
      value: result.isConfirmed,
    };
  }

  /**
   * Traduz uma chave. Se o texto não for uma chave de tradução válida,
   * devolve-o tal como está (evita parênteses retos do ngx-translate).
   */
  private tr(text: string): string {
    if (!text) return '';
    const translated = this.translate.instant(text);
    // ngx-translate devolve [chave] quando não encontra a tradução
    if (translated === `[${text}]`) return PT_FALLBACK[text] || text;
    // Se translate.instant() devolveu algo diferente da chave, a tradução existe
    if (translated !== text) return translated;
    // Fallback PT-PT antes de devolver o texto original
    return PT_FALLBACK[text] || text;
  }

  /**
   * Exibe uma mensagem de erro
   * @param message Mensagem de erro (pode ser chave de tradução)
   * @param title Título (opcional, pode ser chave de tradução)
   */
  showError(message: string, title?: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'error',
        title: title ? this.tr(title) : 'Erro',
        text: this.tr(message),
        confirmButtonText: this.tr('ACTIONS.OK') || 'OK',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Exibe uma mensagem de sucesso
   * @param message Mensagem de sucesso (pode ser chave de tradução)
   * @param title Título (opcional, pode ser chave de tradução)
   * @param timer Tempo em ms para fechar automaticamente (opcional)
   */
  showSuccess(message: string, title?: string, timer?: number): Promise<any> {
    return this.svMessage
      .fire({
        type: 'success',
        title: title ? this.tr(title) : this.tr('COMMON.ALERTS.SUCCESS'),
        text: this.tr(message),
        confirmButtonText: this.tr('ACTIONS.OK') || 'OK',
        timer: timer,
        showConfirmButton: !timer,
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Exibe uma mensagem de aviso
   * @param message Mensagem de aviso (pode ser chave de tradução)
   * @param title Título (opcional, pode ser chave de tradução)
   */
  showWarning(message: string, title?: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'warning',
        title: title ? this.tr(title) : this.tr('COMMON.ALERTS.ATTENTION'),
        text: this.tr(message),
        confirmButtonText: this.tr('ACTIONS.OK') || 'OK',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Exibe uma mensagem informativa
   * @param message Mensagem informativa (pode ser chave de tradução)
   * @param title Título (opcional, pode ser chave de tradução)
   */
  showInfo(message: string, title?: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'info',
        title: title ? this.tr(title) : 'Informação',
        text: this.tr(message),
        confirmButtonText: this.tr('ACTIONS.OK') || 'OK',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Exibe uma mensagem informativa muito leve e não bloqueante (Toast)
   */
  showToastInfo(message: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'info',
        title: 'Informação',
        text: this.tr(message),
        timer: 4000,
        showConfirmButton: false,
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Exibe um diálogo de confirmação
   * @param message Mensagem de confirmação (pode ser chave de tradução)
   * @param title Título (opcional, pode ser chave de tradução)
   * @returns Promise que resolve para true se confirmado, false se cancelado
   */
  async confirm(message: string, title?: string): Promise<boolean> {
    const result = await this.svMessage.fire({
      type: 'question',
      title: title ? this.tr(title) : 'Confirmar',
      text: this.tr(message),
      showCancelButton: true,
      confirmButtonText: this.tr('ACTIONS.YES') || 'Sim',
      cancelButtonText: this.tr('ACTIONS.NO') || 'Não',
      reverseButtons: true,
    });
    return result.isConfirmed;
  }

  // ==========================================
  // Métodos especializados para erros comuns
  // ==========================================

  /**
   * Erro de rede/comunicação com o servidor
   * Mensagem profissional que explica o problema e sugere ação
   */
  showNetworkError(): Promise<any> {
    return this.svMessage
      .fire({
        type: 'error',
        title: 'Erro de Comunicação',
        text: 'Não foi possível contactar o servidor. Verifique a sua ligação à internet e tente novamente.',
        confirmButtonText: 'Entendi',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Erro de validação de dados
   * @param message Mensagem específica (opcional)
   */
  showValidationError(message?: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'warning',
        title: message ? 'Atenção' : 'Dados Inválidos',
        text: message || 'Por favor, verifique os dados inseridos e tente novamente.',
        confirmButtonText: 'OK',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Erro de autorização (401/403)
   */
  showUnauthorizedError(): Promise<any> {
    return this.svMessage
      .fire({
        type: 'error',
        title: 'Acesso Negado',
        text: 'Não tem permissão para realizar esta operação.',
        confirmButtonText: 'Entendi',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Recurso não encontrado (404)
   */
  showNotFoundError(resource?: string): Promise<any> {
    const message = resource ? `${resource} não encontrado.` : 'O recurso solicitado não foi encontrado.';

    return this.svMessage
      .fire({
        type: 'error',
        title: 'Não Encontrado',
        text: message,
        confirmButtonText: 'Entendi',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Erro interno do servidor (500)
   */
  showServerError(): Promise<any> {
    return this.svMessage
      .fire({
        type: 'error',
        title: 'Erro do Servidor',
        text: 'Ocorreu um erro no servidor. Por favor, tente novamente mais tarde ou contacte o suporte.',
        confirmButtonText: 'Entendi',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Erro genérico de pedido falhado
   * Usa mensagem profissional e contextualizada
   * IMPORTANTE: Verifica automaticamente se é problema de rede para evitar mensagens duplicadas
   */
  showRequestFailed(details?: string): Promise<any> {
    // Se não há internet, não mostrar - a barra de rede já está visível
    if (!this.isOnline()) {
      return Promise.resolve({ isConfirmed: false, isDismissed: true, value: false });
    }

    return this.svMessage
      .fire({
        type: 'error',
        title: 'Pedido Falhado',
        text: details || 'Não foi possível processar o seu pedido. Por favor, tente novamente.',
        confirmButtonText: 'Tentar Novamente',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Dados incompletos ou em falta
   */
  showIncompleteData(): Promise<any> {
    return this.svMessage
      .fire({
        type: 'warning',
        title: 'Dados Incompletos',
        text: 'Por favor, preencha todos os campos obrigatórios.',
        confirmButtonText: 'Entendi',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Sessão expirada
   */
  showSessionExpired(): Promise<any> {
    return this.svMessage
      .fire({
        type: 'warning',
        title: 'Sessão Expirada',
        text: 'A sua sessão expirou. Por favor, inicie sessão novamente.',
        confirmButtonText: 'Iniciar Sessão',
      })
      .then(r => this.toSwalResult(r));
  }

  /**
   * Operação bem-sucedida genérica
   */
  showOperationSuccess(message?: string): Promise<any> {
    return this.svMessage
      .fire({
        type: 'success',
        title: 'Sucesso',
        text: message || 'Operação realizada com sucesso.',
        timer: 3000,
        showConfirmButton: false,
      })
      .then(r => this.toSwalResult(r));
  }

  // ==========================================
  // Métodos para casos de uso específicos
  // ==========================================

  /**
   * Confirmação de eliminação
   * @param itemName Nome do item a eliminar
   */
  async confirmDelete(itemName?: string): Promise<boolean> {
    const message = itemName
      ? `Tem a certeza que deseja eliminar "${itemName}"?`
      : 'Tem a certeza que deseja eliminar este item?';

    const result = await this.svMessage.fire({
      type: 'warning',
      title: 'Confirmar Eliminação',
      text: message,
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });
    return result.isConfirmed;
  }

  /**
   * Mensagem HTML personalizada (usar com cuidado)
   * @param html Conteúdo HTML
   * @param title Título
   * @param icon Ícone
   */
  showCustomHtml(
    html: string,
    title?: string,
    icon: 'success' | 'error' | 'warning' | 'info' | 'question' = 'info',
  ): Promise<any> {
    return this.svMessage
      .fire({
        type: icon,
        title: title,
        html,
        confirmButtonText: 'OK',
      })
      .then(r => this.toSwalResult(r));
  }
}
